import dayjs, { type Dayjs } from "dayjs";

/**
 * Every rule a flow schema can put on a `date-picker` field, in one place.
 *
 * Ported from admin-console's `src/components/VMRS/utils.ts` so the preview an
 * operator sees while configuring a provider and the picker an end user gets
 * enforce the same thing. A rule honoured on only one side is worse than no
 * rule: it lets an order be built that the preview said was impossible.
 *
 * The schema keys understood here:
 *
 *   min_date / max_date              absolute bounds
 *   date_restriction: future | past  bounds relative to now
 *   min_lead_hours / max_lead_hours  bounds relative to now, finer than a day
 *   min_range_days / max_range_days  gap to the paired field
 *   min_range_hours / max_range_hours   the same, finer than a day
 *   range_with                       which field is the pair
 *   show_time / time_format          whether the picker asks for a time
 */

/**
 * Resolve the partner field key for a paired date range. Precedence:
 *   1. `options[fieldKey].range_with` — explicit pointer.
 *   2. Any other key whose `.range_with === fieldKey` — reverse pointer.
 *   3. Back-compat: the classic `date_from` ↔ `date_to` convention.
 * Returns null when no partner is configured.
 */
export const resolveRangePartnerKey = (
   fieldKey: string,
   options: Record<string, any>,
): string | null =>
{
   // Only honour an explicit pointer if the target exists — this guards against
   // a typo ("date_form") silently dropping the constraint.
   const direct = options?.[fieldKey]?.range_with;
   if (typeof direct === "string" && direct && options?.[direct]) return direct;

   for (const [key, config] of Object.entries(options || {}))
   {
      if (key !== fieldKey && (config as any)?.range_with === fieldKey) return key;
   }

   if (fieldKey === "date_from" && options?.date_to) return "date_to";
   if (fieldKey === "date_to" && options?.date_from) return "date_from";

   return null;
};

/**
 * Which half of a pair a field is. Decided by name, because `range_with` may be
 * declared on either side and so says nothing about direction. Anything that
 * does not read as an end date counts as the start.
 */
const nameSide = (fieldKey: string): "from" | "to" | null =>
{
   const key = fieldKey.toLowerCase();
   if (/(^|_)(from|start|begin)(_|$)/.test(key)) return "from";
   if (/(^|_)(to|end|finish)(_|$)/.test(key)) return "to";
   return null;
};

/**
 * When `options` is given and neither name says which half a field is
 * (`request_date` / `collection_time`), the field that declares `range_with`
 * is the dependent one — the end — and the field it points at is the start.
 * Without that, both would count as starts and no gap would ever be enforced.
 */
export const isRangeFromSide = (fieldKey: string, options?: Record<string, any>): boolean =>
{
   const own = nameSide(fieldKey);
   if (own) return own === "from";
   if (!options) return true;

   const partnerKey = resolveRangePartnerKey(fieldKey, options);
   if (!partnerKey) return true;

   const partner = nameSide(partnerKey);
   if (partner) return partner === "to";

   return options?.[fieldKey]?.range_with !== partnerKey;
};

/**
 * Gap to the paired field, in hours.
 *
 * Days are the unit on the config screen because these windows are day-scale in
 * practice; the `_hours` twin is the raw-JSON escape hatch for a provider that
 * needs finer, and it wins when both are present. Either side of the pair may
 * carry the setting.
 */
const resolveRangeGapHours = (
   bound: "min" | "max",
   ownConfig: any,
   partnerConfig: any,
): number | undefined =>
{
   const hours = ownConfig?.[`${bound}_range_hours`] ?? partnerConfig?.[`${bound}_range_hours`];
   if (typeof hours === "number") return hours;

   const days = ownConfig?.[`${bound}_range_days`] ?? partnerConfig?.[`${bound}_range_days`];
   return typeof days === "number" ? days * 24 : undefined;
};

export interface DateFieldBounds
{
   min: Dayjs | null;
   max: Dayjs | null;
}

/**
 * The single window a field may be picked in, folding together every rule that
 * can narrow it.
 *
 * Both `disabledDate` and `disabledTime` come from this one window, so a
 * time-enabled field is trimmed to the hour rather than the whole day.
 *
 * Only the END side is bound to its partner. The start stays free and the pair
 * is kept consistent by clearing a partner that has gone stale (see
 * `isPartnerValueStale`) — binding both sides instead would trap a user who
 * wants to widen a range, since the start could not move past the end.
 */
export const getDateFieldBounds = (params: {
   fieldKey: string;
   config: any;
   formData: Record<string, any>;
   options: Record<string, any>;
   /** Extra floor/ceiling the host screen imposes, e.g. archive being past-only. */
   minExtra?: Dayjs | null;
   maxExtra?: Dayjs | null;
   now?: Dayjs;
}): DateFieldBounds =>
{
   const { fieldKey, config, formData, options, minExtra, maxExtra, now = dayjs() } = params;

   const mins: Dayjs[] = [];
   const maxs: Dayjs[] = [];

   if (minExtra) mins.push(minExtra);
   if (maxExtra) maxs.push(maxExtra);

   if (config?.min_date) mins.push(dayjs(config.min_date));
   if (config?.max_date) maxs.push(dayjs(config.max_date));

   if (config?.date_restriction === "future") mins.push(now.startOf("day"));
   if (config?.date_restriction === "past") maxs.push(now.endOf("day"));

   if (typeof config?.min_lead_hours === "number") mins.push(now.add(config.min_lead_hours, "hour"));
   if (typeof config?.max_lead_hours === "number") maxs.push(now.add(config.max_lead_hours, "hour"));

   const partnerKey = resolveRangePartnerKey(fieldKey, options);
   const partnerValue = partnerKey ? formData?.[partnerKey] : null;

   if (partnerKey && partnerValue && !isRangeFromSide(fieldKey, options))
   {
      const partnerConfig = options?.[partnerKey];
      const other = dayjs(partnerValue);
      const minGap = resolveRangeGapHours("min", config, partnerConfig);
      const maxGap = resolveRangeGapHours("max", config, partnerConfig);

      // No configured gap still means "not before the start" — ordering alone.
      mins.push(typeof minGap === "number" ? other.add(minGap, "hour") : other);
      if (typeof maxGap === "number") maxs.push(other.add(maxGap, "hour"));
   }

   return {
      min: mins.length ? mins.reduce((acc, item) => (item.isAfter(acc) ? item : acc)) : null,
      max: maxs.length ? maxs.reduce((acc, item) => (item.isBefore(acc) ? item : acc)) : null,
   };
};

/**
 * A day is off-limits when no pickable moment is left in it. Without a time
 * panel the only pickable moment is midnight, so the day is judged by that
 * single instant.
 */
export const buildDisabledDate = (bounds: DateFieldBounds, showTime: boolean) =>
{
   if (!bounds.min && !bounds.max) return undefined;

   return (current: Dayjs) =>
   {
      if (!current) return false;

      if (!showTime)
      {
         const candidate = current.startOf("day");
         return !!(
            (bounds.min && candidate.isBefore(bounds.min)) ||
            (bounds.max && candidate.isAfter(bounds.max))
         );
      }

      return !!(
         (bounds.min && current.endOf("day").isBefore(bounds.min)) ||
         (bounds.max && current.startOf("day").isAfter(bounds.max))
      );
   };
};

const rangeOfNumbers = (from: number, to: number): number[] =>
{
   const result: number[] = [];
   for (let i = from; i <= to; i += 1) result.push(i);
   return result;
};

/**
 * Hour-level trim of the boundary days: on the first allowed day everything
 * before the floor is greyed out, on the last one everything after the ceiling.
 * Days in between are untouched — `disabledDate` has already dropped the ones
 * that fall fully outside.
 */
export const buildDisabledTime = (bounds: DateFieldBounds, showTime: boolean) =>
{
   if (!showTime || (!bounds.min && !bounds.max)) return undefined;

   return (current: Dayjs | null) =>
   {
      if (!current) return {};

      const lower = bounds.min && current.isSame(bounds.min, "day") ? bounds.min : null;
      const upper = bounds.max && current.isSame(bounds.max, "day") ? bounds.max : null;
      if (!lower && !upper) return {};

      return {
         disabledHours: () => [
            ...(lower ? rangeOfNumbers(0, lower.hour() - 1) : []),
            ...(upper ? rangeOfNumbers(upper.hour() + 1, 23) : []),
         ],
         disabledMinutes: (hour: number) => [
            ...(lower && hour === lower.hour() ? rangeOfNumbers(0, lower.minute() - 1) : []),
            ...(upper && hour === upper.hour() ? rangeOfNumbers(upper.minute() + 1, 59) : []),
         ],
         disabledSeconds: (hour: number, minute: number) => [
            ...(lower && hour === lower.hour() && minute === lower.minute()
               ? rangeOfNumbers(0, lower.second() - 1)
               : []),
            ...(upper && hour === upper.hour() && minute === upper.minute()
               ? rangeOfNumbers(upper.second() + 1, 59)
               : []),
         ],
      };
   };
};

/**
 * After the start side moves, the end side may have fallen outside its window.
 * Only then is it worth dropping — a still-valid pick must survive the edit.
 * `formData` must already carry the new start value.
 */
export const isPartnerValueStale = (params: {
   partnerKey: string;
   formData: Record<string, any>;
   options: Record<string, any>;
   minExtra?: Dayjs | null;
   maxExtra?: Dayjs | null;
}): boolean =>
{
   const { partnerKey, formData, options, minExtra, maxExtra } = params;

   const partnerValue = formData?.[partnerKey];
   if (!partnerValue) return false;

   const bounds = getDateFieldBounds({
      fieldKey: partnerKey,
      config: options?.[partnerKey],
      formData,
      options,
      minExtra,
      maxExtra,
   });
   const value = dayjs(partnerValue);

   return !!(
      (bounds.min && value.isBefore(bounds.min)) ||
      (bounds.max && value.isAfter(bounds.max))
   );
};

/** `DD/MM/YYYY`, plus the time part when the field asks for one. */
export const getDateDisplayFormat = (config: any): string =>
   config?.show_time ? `DD/MM/YYYY ${config?.time_format ?? "HH:mm"}` : "DD/MM/YYYY";

/**
 * One line of the rule, ready for `t()`.
 *
 * Structured rather than pre-rendered because this module has no i18n of its
 * own; the caller turns each one into text.
 */
export interface DateRuleHint
{
   i18nKey: string;
   values: Record<string, unknown>;
   /** A field title that still needs translating before interpolation. */
   partnerLabel?: string;
}

const formatBound = (value: Dayjs, showTime: boolean): string =>
   value.format(showTime ? "DD/MM/YYYY HH:mm" : "DD/MM/YYYY");

/**
 * Explain, in the field's own label, why days are greyed out.
 *
 * Derived from the SAME inputs as the picker's window, and attached to the
 * field the rule actually constrains rather than the one that happens to
 * declare it — a `min_range_days` written on `date_from` restricts `date_to`,
 * so hinting it on `date_from` leaves the user staring at a disabled calendar
 * with no explanation.
 *
 * The gap to a partner is described symbolically ("at least 7 days after Start
 * date") because it holds before anything is picked. Everything else is
 * described as the concrete window it resolves to, which is what the user is
 * about to click.
 */
export const getDateFieldRuleHints = (params: {
   fieldKey: string;
   config: any;
   formData: Record<string, any>;
   options: Record<string, any>;
   minExtra?: Dayjs | null;
   maxExtra?: Dayjs | null;
   now?: Dayjs;
}): DateRuleHint[] =>
{
   const { fieldKey, config, options } = params;
   const hints: DateRuleHint[] = [];
   const showTime = !!config?.show_time;

   const partnerKey = resolveRangePartnerKey(fieldKey, options);
   const isEndSide = !!partnerKey && !isRangeFromSide(fieldKey, options);

   if (isEndSide)
   {
      const partnerConfig = options?.[partnerKey as string];
      const partnerLabel =
         partnerConfig?.title || partnerConfig?.label || (partnerKey as string);

      // Days are the unit operators configure in, so report days unless only
      // the _hours twin was set — echoing back a unit nobody typed is confusing.
      const gap = (bound: "min" | "max") =>
      {
         const hours = config?.[`${bound}_range_hours`] ?? partnerConfig?.[`${bound}_range_hours`];
         if (typeof hours === "number") return { amount: hours, unit: "hours" as const };

         const days = config?.[`${bound}_range_days`] ?? partnerConfig?.[`${bound}_range_days`];
         return typeof days === "number" ? { amount: days, unit: "days" as const } : null;
      };

      const minGap = gap("min");
      const maxGap = gap("max");

      if (minGap && maxGap && minGap.unit === maxGap.unit)
      {
         hints.push({
            i18nKey:
               minGap.unit === "days"
                  ? "{{min}}–{{max}} days after {{field}}"
                  : "{{min}}–{{max}} hours after {{field}}",
            values: { min: minGap.amount, max: maxGap.amount },
            partnerLabel,
         });
      }
      else
      {
         if (minGap)
         {
            hints.push({
               i18nKey:
                  minGap.unit === "days"
                     ? "at least {{amount}} days after {{field}}"
                     : "at least {{amount}} hours after {{field}}",
               values: { amount: minGap.amount },
               partnerLabel,
            });
         }
         if (maxGap)
         {
            hints.push({
               i18nKey:
                  maxGap.unit === "days"
                     ? "within {{amount}} days of {{field}}"
                     : "within {{amount}} hours of {{field}}",
               values: { amount: maxGap.amount },
               partnerLabel,
            });
         }
      }
   }

   // Everything that is NOT the partner gap, as the window it resolves to. Run
   // without formData so a partner value cannot fold into this line — it is
   // already covered symbolically above.
   const standalone = getDateFieldBounds({ ...params, formData: {} });

   if (standalone.min && standalone.max)
   {
      hints.push({
         i18nKey: "between {{from}} and {{to}}",
         values: {
            from: formatBound(standalone.min, showTime),
            to: formatBound(standalone.max, showTime),
         },
      });
   }
   else if (standalone.min)
   {
      hints.push({
         i18nKey: "from {{from}}",
         values: { from: formatBound(standalone.min, showTime) },
      });
   }
   else if (standalone.max)
   {
      hints.push({
         i18nKey: "until {{to}}",
         values: { to: formatBound(standalone.max, showTime) },
      });
   }

   return hints;
};
